import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AudioRecorder from '../components/AudioRecorder';
import SummaryDisplay from '../components/SummaryDisplay';
import RealtimeQuestionsDisplay from '../components/RealtimeQuestionsDisplay';
import LLMOutput from '../components/LLMOutput';
import ConversationLog from '../components/ConversationLog';
import SessionReportModal from '../components/SessionReportModal';
import FeedbackModal from '../components/FeedbackModal';
import InteractionWarnings from '../components/InteractionWarnings';
import CDSChat from '../components/CDSChat';
import { useSettings } from '../context/SettingsContext';
import { useSessions } from '../context/SessionContext';
import { useBookmarks } from '../context/BookmarkContext';
import { useAuth } from '../context/AuthContext';
import { checkInteractions, cleanupText, fetchPatients, fetchPatientHistory, archiveSessionToBackend } from '../services/api';

function AnalysePage() {
  const { settings } = useSettings();
  const retentionDays = settings.sessionRetentionDays || 180;
  const { saveSession, loadSession } = useSessions();
  const { addBookmark, removeBookmark, isBookmarked, findBookmark } = useBookmarks();
  const { user } = useAuth();
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [transcription, setTranscription] = useState('');
  const [summary, setSummary] = useState(null);
  const [realtimeQuestions, setRealtimeQuestions] = useState(null);
  const [llmResult, setLLMResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [summaryUpdating, setSummaryUpdating] = useState(false);
  const [questionsUpdating, setQuestionsUpdating] = useState(false);
  const [analysisUpdating, setAnalysisUpdating] = useState(false);
  const [manualNotes, setManualNotes] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [reportSessionName, setReportSessionName] = useState('');
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  const [showNoDiagnosisWarning, setShowNoDiagnosisWarning] = useState(false);
  const [recorderKey, setRecorderKey] = useState(0);
  const [conversationLog, setConversationLog] = useState([]);
  const [speakerNames, setSpeakerNames] = useState({});
  const [interactions, setInteractions] = useState(null);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const lastCheckedMedsRef = useRef('');
  const [patientId, setPatientId] = useState('');
  const patientIdRef = useRef('');
  const [existingPatients, setExistingPatients] = useState([]);
  const [patientContext, setPatientContext] = useState(null);
  const historyLoadGenRef = useRef(0);

  // Dokument-Upload
  const [sessionIsActive, setSessionIsActive] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Speaker-turn cleanup tracking
  const pendingTurnRef = useRef(null); // { id, rawText, speaker }
  const cleanupTimerRef = useRef(null);

  // Wechselwirkungsprüfung vorerst deaktiviert (auf Wunsch, nicht nur ausgeblendet —
  // die Prüfung wird nicht mehr ausgeführt). Siehe docs/pvs-datenqualitaet-checkliste.md,
  // Kategorie 6, zur Einordnung der bisherigen LLM-basierten Prüfung.
  // useEffect(() => {
  //   if (!llmResult) return;
  //
  //   const medTitles = ['aktuelle medikation', 'verordnete medikation', 'medikament'];
  //
  //   // Was der Patient aktuell einnimmt (aus Anamnese-Zusammenfassung)
  //   const currentMeds = (summary || [])
  //     .filter(s => medTitles.some(t => s.title?.toLowerCase().includes(t)))
  //     .flatMap(s => s.items || [])
  //     .filter(Boolean);
  //
  //   // Was der Arzt vorschlägt (aus Differentialdiagnosen)
  //   const proposedMeds = llmResult
  //     .flatMap(d => d.medications || [])
  //     .filter(Boolean);
  //
  //   const medsKey = [...currentMeds].sort().join('|') + '§' + [...proposedMeds].sort().join('|');
  //   if (medsKey === lastCheckedMedsRef.current) return;
  //   lastCheckedMedsRef.current = medsKey;
  //
  //   if (currentMeds.length + proposedMeds.length < 2) {
  //     setInteractions(null);
  //     return;
  //   }
  //
  //   setInteractionsLoading(true);
  //   checkInteractions(currentMeds, proposedMeds)
  //     .then(data => setInteractions(data.warnings))
  //     .catch(() => setInteractions([]))
  //     .finally(() => setInteractionsLoading(false));
  // }, [summary, llmResult]);

  // Track loaded session for resume
  const [resumeSessionName, setResumeSessionName] = useState(null);
  const [initialTranscript, setInitialTranscript] = useState(null);
  const loadedSessionRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const loadSessionRef = useRef(loadSession);
  useEffect(() => { loadSessionRef.current = loadSession; }, [loadSession]);

  // Load session data when sessionId is present
  // loadSession absichtlich NICHT in deps — Änderungen am sessions-Store sollen
  // keine laufende Session neu laden und damit Inhalte überschreiben.
  useEffect(() => {
    if (sessionId && !hasLoadedRef.current) {
      const session = loadSessionRef.current(sessionId);
      if (session) {
        loadedSessionRef.current = session;
        hasLoadedRef.current = true;
        setTranscription(session.transcription || '');
        setSummary(session.summary || null);
        setRealtimeQuestions(session.questions || null);
        setLLMResult(session.diagnoses || null);
        setManualNotes(session.manualNotes || '');
        setPrivateNotes(session.privateNotes || '');
        setConversationLog(session.conversationLog || []);
        setSpeakerNames(session.speakerNames || {});
        setUploadedDocs(session.uploadedDocs || []);
        setResumeSessionName(session.name);
        setInitialTranscript(session.transcription || '');
        setSessionIsActive(true);
      }
    }
    if (!sessionId) {
      hasLoadedRef.current = false;
      loadedSessionRef.current = null;
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const collectSessionData = (name, keepPrivateNotes = true) => ({
    id: loadedSessionRef.current?.id || sessionId || undefined,
    name,
    createdAt: loadedSessionRef.current?.createdAt || undefined,
    transcription,
    summary,
    diagnoses: llmResult,
    questions: realtimeQuestions,
    manualNotes,
    privateNotes: keepPrivateNotes ? privateNotes : '',
    conversationLog,
    speakerNames,
    uploadedDocs: uploadedDocs.map(({ id, name: n, size, category, status, content }) => ({ id, name: n, size, category, status, content })),
  });

  const doCleanup = async (turn) => {
    try {
      const cleaned = await cleanupText(turn.rawText);
      if (cleaned && cleaned.trim()) {
        setConversationLog(prev => prev.map(e =>
          e.id === turn.id ? { ...e, text: cleaned.trim() } : e
        ));
      }
    } catch {
      // Rohtext bleibt sichtbar wenn Cleanup fehlschlägt
    }
  };

  const handleSegmentTranscribed = (text, timestamp, backendSpeaker) => {
    setConversationLog(prev => {
      const last = prev.length > 0 ? prev[prev.length - 1] : null;
      let speaker;
      if (backendSpeaker) {
        speaker = backendSpeaker;
      } else {
        speaker = last?.speaker === 'arzt' ? 'person_1' : 'arzt';
      }

      // Aufeinanderfolgende Segmente desselben Sprechers zusammenführen
      if (last && last.speaker === speaker && (timestamp - last.timestamp) < 15000) {
        const newRaw = (pendingTurnRef.current?.rawText || last.text) + ' ' + text;
        pendingTurnRef.current = { id: last.id, rawText: newRaw, speaker };
        clearTimeout(cleanupTimerRef.current);
        // Spätestens nach 60s Cleanup auslösen
        cleanupTimerRef.current = setTimeout(() => {
          const turn = pendingTurnRef.current;
          if (turn) { pendingTurnRef.current = null; doCleanup(turn); }
        }, 60000);
        return prev.map((e, i) =>
          i === prev.length - 1 ? { ...e, text: e.text + ' ' + text } : e
        );
      }

      // Sprecherwechsel → laufenden Turn bereinigen
      const prevTurn = pendingTurnRef.current;
      if (prevTurn) {
        clearTimeout(cleanupTimerRef.current);
        pendingTurnRef.current = null;
        setTimeout(() => doCleanup(prevTurn), 0);
      }

      const newEntry = { id: `${timestamp}-${Math.random()}`, speaker, text, timestamp };
      pendingTurnRef.current = { id: newEntry.id, rawText: text, speaker };
      cleanupTimerRef.current = setTimeout(() => {
        const turn = pendingTurnRef.current;
        if (turn) { pendingTurnRef.current = null; doCleanup(turn); }
      }, 60000);
      return [...prev, newEntry];
    });
  };

  const handleReassignEntry = (id, newSpeaker) => {
    setConversationLog(prev => prev.map(e =>
      e.id === id ? { ...e, speaker: newSpeaker } : e
    ));
  };

  const handleRenameSpeaker = (speakerKey, newName) => {
    setSpeakerNames(prev => ({ ...prev, [speakerKey]: newName }));
  };

  const loadPatientHistory = async (label) => {
    // Immer zuerst nullen — verhindert Kontamination bei leerer Patient-ID
    setPatientContext(null);
    if (!label) return;
    // Generation-Counter verhindert Race-Conditions: ein veralteter Fetch
    // darf keinen früheren clearState()-Aufruf rückgängig machen.
    const gen = ++historyLoadGenRef.current;
    try {
      const sessions = await fetchPatientHistory(label);
      if (gen !== historyLoadGenRef.current) return; // veraltetes Ergebnis verwerfen
      if (!sessions || sessions.length === 0) { setPatientContext(null); return; }
      const lines = sessions.map((s) => {
        const date = new Date(s.session_date).toLocaleDateString('de-DE');
        const diags = (s.diagnoses || []).map((d) => d.name).filter(Boolean).join(', ');
        const meds = (s.summary || [])
          .filter((sec) => sec.title?.toLowerCase().includes('medikation'))
          .flatMap((sec) => sec.items || []).join(', ');
        return `[${date}] Diagnosen: ${diags || '—'}${meds ? ` | Medikation: ${meds}` : ''}`;
      });
      setPatientContext(lines.join('\n'));
    } catch {
      if (gen === historyLoadGenRef.current) setPatientContext(null);
    }
  };

  const clearState = () => {
    historyLoadGenRef.current++; // laufende fetchPatientHistory-Aufrufe ungültig machen
    setRecorderKey(k => k + 1);
    setTranscription('');
    setSummary(null);
    setRealtimeQuestions(null);
    setLLMResult(null);
    setManualNotes('');
    setPrivateNotes('');
    setSelectedDiagnoses([]);
    setConversationLog([]);
    setSpeakerNames({});
    setInteractions(null);
    setInteractionsLoading(false);
    lastCheckedMedsRef.current = '';
    setPatientId('');
    patientIdRef.current = '';
    setPatientContext(null);
    setResumeSessionName(null);
    setInitialTranscript(null);
    loadedSessionRef.current = null;
    hasLoadedRef.current = false;
    setUploadedDocs([]);
    setSessionIsActive(false);
  };

  const handleSessionPause = (name) => {
    const data = collectSessionData(name);
    saveSession(data, 'open');
    clearState();
    navigate('/offene-sessions');
  };

  const handleSessionComplete = (name) => {
    setReportSessionName(name);
    if (selectedDiagnoses.length === 0 && llmResult && llmResult.length > 0) {
      setShowNoDiagnosisWarning(true);
    } else {
      setShowReport(true);
    }
  };

  const handleReportConfirm = () => {
    const data = collectSessionData(reportSessionName);
    saveSession(data, 'archived');
    const pid = patientIdRef.current.trim();
    if (pid) {
      archiveSessionToBackend(
        pid, reportSessionName,
        data.summary, data.diagnoses, retentionDays, data.privateNotes, data.uploadedDocs
      ).then(() => {
        console.log('[ARCHIV] Session erfolgreich gespeichert für Patient:', pid);
      }).catch((e) => {
        console.error('[ARCHIV] Backend-Archivierung fehlgeschlagen:', e);
      });
    }
    setShowReport(false);
    // Feedback-Abfrage nach Konsultationsabschluss vorerst deaktiviert (auf Wunsch).
    // setShowFeedback(true);
    handleFeedbackDone();
  };

  const handleFeedbackDone = () => {
    setShowFeedback(false);
    clearState();
    if (sessionId) {
      navigate('/analyse', { replace: true });
    }
  };

  const handleDiagnosisSelect = (diagnosisName) => {
    setSelectedDiagnoses((prev) => {
      if (prev.includes(diagnosisName)) {
        return prev.filter((n) => n !== diagnosisName);
      }
      return [...prev, diagnosisName];
    });
  };

  const handleAdoptIntoNotes = (content) => {
    const marker = `[Durch KI ergänzt] ${content}`;
    setManualNotes((prev) => (prev ? `${prev}\n\n${marker}` : marker));
  };

  const handleBookmark = ({ diagnosisName, sectionLabel, itemText }) => {
    const existing = findBookmark(diagnosisName, sectionLabel, itemText);
    if (existing) {
      removeBookmark(existing.id);
    } else {
      addBookmark({ diagnosisName, sectionLabel, itemText });
    }
  };

  // ── Dokument-Upload ─────────────────────────────────────────────
  const classifyFile = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['docx', 'doc'].includes(ext)) return 'docx';
    if (['jpg', 'jpeg', 'png', 'tiff', 'tif', 'bmp'].includes(ext)) return 'image';
    if (['txt', 'csv'].includes(ext)) return 'text';
    return 'other';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const processFiles = useCallback((files) => {
    const newDocs = Array.from(files).map((file) => {
      const category = classifyFile(file.name);
      return {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        name: file.name,
        size: file.size,
        category,
        status: category === 'text' ? 'bereit' : 'ausstehend',
        content: null,
        file,
      };
    });

    newDocs.forEach((doc) => {
      if (doc.category === 'text') {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedDocs((prev) =>
            prev.map((d) => (d.id === doc.id ? { ...d, content: e.target.result } : d))
          );
        };
        reader.readAsText(doc.file);
      }
    });

    setUploadedDocs((prev) => [...prev, ...newDocs]);
    setShowUploadModal(false);
  }, []);

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
  };

  const removeDoc = (id) => setUploadedDocs((prev) => prev.filter((d) => d.id !== id));

  const DOC_CATEGORY_LABEL = { pdf: 'PDF', docx: 'Word-Dokument', image: 'Bilddatei', text: 'Textdatei', other: 'Datei' };

  const DocIcon = ({ category }) => {
    const icons = {
      pdf: <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>,
      docx: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></>,
      image: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>,
      text: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></>,
      other: <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>,
    };
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {icons[category] || icons.other}
      </svg>
    );
  };

  return (
    <div className="page page-wide">
      <h1 className="page-title">Konsultation</h1>
      <p className="page-subtitle">Assistenz im Gespräch mit Patient:innen</p>

      <AudioRecorder
        key={recorderKey}
        onTranscription={setTranscription}
        onSummary={setSummary}
        onQuestions={setRealtimeQuestions}
        onLLMResult={setLLMResult}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
        setSummaryUpdating={setSummaryUpdating}
        setQuestionsUpdating={setQuestionsUpdating}
        setAnalysisUpdating={setAnalysisUpdating}
        manualNotes={manualNotes}
        numDiagnoses={settings.numDiagnoses}
        onSessionStart={(name, pid) => { patientIdRef.current = pid || ''; setPatientId(pid || ''); loadPatientHistory(pid); setSessionIsActive(true); }}
        existingPatients={existingPatients}
        onRequestPatients={() => fetchPatients().then(setExistingPatients).catch(() => {})}
        patientContext={patientContext}
        onSessionPause={handleSessionPause}
        onSessionComplete={handleSessionComplete}
        resumeSessionName={resumeSessionName}
        initialTranscript={initialTranscript}
        onSegmentTranscribed={handleSegmentTranscribed}
      />

      <ConversationLog
        entries={conversationLog}
        doctorName={user?.name}
      />

      {/* ── Befunddokumente ── */}
      <div className="card">
        <div className="doc-upload-header">
          <h2>Befunddokumente</h2>
          <button
            className="doc-upload-btn"
            onClick={() => sessionIsActive && setShowUploadModal(true)}
            disabled={!sessionIsActive}
            title={!sessionIsActive ? 'Starten Sie zuerst eine Konsultation' : 'Dokument hinzufügen'}
            style={{ opacity: sessionIsActive ? 1 : 0.45, cursor: sessionIsActive ? 'pointer' : 'not-allowed' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Dokument hinzufügen
          </button>
        </div>

        {uploadedDocs.length === 0 ? (
          <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 10 }}>
            {sessionIsActive
              ? 'Laborwerte, Arztbriefe und weitere Befunde hochladen — fließen automatisch in die Analyse ein.'
              : 'Verfügbar nach dem Starten einer Konsultation. Laborbefunde und Arztbriefe fließen direkt in die Analyse ein.'}
          </p>
        ) : (
          <div className="doc-list">
            {uploadedDocs.map((doc) => (
              <div key={doc.id} className="doc-item">
                <div className={`doc-item-icon ${doc.category}`}>
                  <DocIcon category={doc.category} />
                </div>
                <div className="doc-item-info">
                  <span className="doc-item-name">{doc.name}</span>
                  <span className="doc-item-meta">{formatFileSize(doc.size)} · {DOC_CATEGORY_LABEL[doc.category]}</span>
                </div>
                <span className={`doc-item-status ${doc.status}`}>
                  {doc.status === 'bereit' ? 'Bereit' : 'Ausstehend'}
                </span>
                <button className="doc-item-remove" onClick={() => removeDoc(doc.id)} title="Entfernen">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="top-results">
        <div className="fixed-lg">
          <SummaryDisplay
            summary={summary}
            isProcessing={isProcessing}
            isUpdating={summaryUpdating}
          />
        </div>
        <div className="fixed-lg">
          <RealtimeQuestionsDisplay
            questions={realtimeQuestions}
            isProcessing={isProcessing}
            isUpdating={questionsUpdating}
          />
        </div>

        <div className="top-results-span">
          <CDSChat
            transcript={transcription}
            summary={summary}
            diagnoses={llmResult}
            patientContext={patientContext}
            onAdopt={handleAdoptIntoNotes}
          />
        </div>

        <div className="card manual-notes-card">
          <h2>Ergänzungen</h2>
          <textarea
            className="manual-notes-input"
            placeholder="Hier können Sie zusätzliche Informationen eintragen, die in die Zusammenfassung einfließen..."
            value={manualNotes}
            onChange={(e) => setManualNotes(e.target.value)}
          />
        </div>
        <div className="card private-notes-card">
          <div className="private-notes-header">
            <h2>Interne Notizen</h2>
            <span className="private-notes-badge">Nur für Sie — nicht im Bericht</span>
          </div>
          <textarea
            className="private-notes-input"
            placeholder="Offene Fragen, Rücksprachen, Erinnerungen... (nur für Sie sichtbar, nicht im Bericht)"
            value={privateNotes}
            onChange={(e) => setPrivateNotes(e.target.value)}
          />
        </div>
      </div>

      {/* Wechselwirkungsprüfung vorerst ausgeblendet, siehe Hinweis am useEffect oben. */}
      {false && (
        <InteractionWarnings
          warnings={interactions}
          isLoading={interactionsLoading}
          medicationCount={0}
        />
      )}

      <LLMOutput
        result={llmResult}
        isProcessing={isProcessing}
        isUpdating={analysisUpdating}
        onBookmark={handleBookmark}
        isBookmarked={isBookmarked}
        selectedDiagnoses={selectedDiagnoses}
        onDiagnosisSelect={handleDiagnosisSelect}
      />

      {showNoDiagnosisWarning && (
        <div className="session-modal-overlay" onClick={() => setShowNoDiagnosisWarning(false)}>
          <div className="session-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Keine Diagnose ausgewählt</h3>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px', lineHeight: '1.5' }}>
              Sie haben keine Diagnose ausgewählt. Möchten Sie zurückgehen und eine Haupt- oder Nebendiagnose auswählen, oder ohne Diagnoseauswahl fortfahren?
            </p>
            <div className="session-modal-actions">
              <button className="modal-btn-cancel" onClick={() => setShowNoDiagnosisWarning(false)}>
                Zurück zur Auswahl
              </button>
              <button
                className="modal-btn-confirm"
                onClick={() => {
                  setShowNoDiagnosisWarning(false);
                  setShowReport(true);
                }}
              >
                Ohne Diagnose fortfahren
              </button>
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <SessionReportModal
          session={{
            name: reportSessionName,
            summary,
            diagnoses: llmResult,
            manualNotes,
            selectedDiagnoses,
          }}
          onClose={() => setShowReport(false)}
          onConfirm={handleReportConfirm}
        />
      )}

      {/* Feedback-Abfrage nach Konsultationsabschluss vorerst deaktiviert (auf Wunsch).
      {showFeedback && (
        <FeedbackModal
          onSkip={handleFeedbackDone}
          onSubmit={handleFeedbackDone}
        />
      )}
      */}

      {/* ── Upload-Modal ── */}
      {showUploadModal && (
        <div className="session-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div
            className="session-modal"
            style={{ maxWidth: 520, width: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Befunddokument hinzufügen</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '6px 0 0 0' }}>
              Laborbefunde, Arztbriefe und Befundberichte werden direkt ausgewertet.
            </p>

            <div
              className={`upload-dropzone${isDragOver ? ' drag-over' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="upload-dropzone-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <span className="upload-dropzone-text">Dateien hierher ziehen oder klicken zum Auswählen</span>
              <span className="upload-dropzone-hint">PDF, DOCX, JPG, PNG, TXT · Mehrfachauswahl möglich</span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.tif,.txt,.csv"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />

            <div className="upload-note">
              Laborwerte und Arztbriefe (PDF, DOCX, TXT) werden automatisch aus dem Text extrahiert und in die Differentialdiagnose einbezogen.
            </div>
            <div className="upload-note-imaging">
              Bildgebende Befunde (CT, MRT, Röntgen) können aktuell nur als Referenz gespeichert werden. Die automatische Bildauswertung erfordert spezialisierte KI-Modelle und ist für einen späteren Schritt geplant.
            </div>

            <div className="session-modal-actions" style={{ marginTop: 20 }}>
              <button className="modal-btn-cancel" onClick={() => setShowUploadModal(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysePage;
